import { Component, Inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

interface ClientMessageDialogData {
  client: {
    customerName: string;
    contactNumber: string;
    totalOrders: number;
    totalSpend: number;
    lastOrderDate?: string;
    lastPickupLocation?: string;
    lastDeliveryLocation?: string;
  };
}

@Component({
  selector: 'app-client-message-dialog',
  templateUrl: './client-message-dialog.component.html',
  styleUrls: ['./client-message-dialog.component.scss']
})
export class ClientMessageDialogComponent {
  readonly templates = [
    {
      label: 'Delivery update',
      subject: 'Update on your delivery request',
      message: 'Hello, this is an update from MAKH Logistics regarding your delivery request. Please reply if you need more details.'
    },
    {
      label: 'Payment reminder',
      subject: 'Payment reminder for your delivery',
      message: 'Hello, this is a reminder that payment is still pending for your recent delivery order. Please contact MAKH Logistics if you need assistance.'
    },
    {
      label: 'Follow-up',
      subject: 'Follow-up from MAKH Logistics',
      message: 'Hello, thank you for choosing MAKH Logistics. We are following up on your recent delivery experience and are available if you need anything else.'
    }
  ];

  form = this.fb.group({
    sendSms: [true],
    sendEmail: [false],
    email: [''],
    subject: [''],
    message: ['', [Validators.required, Validators.maxLength(1000)]]
  });

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ClientMessageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ClientMessageDialogData
  ) {}

  get messageLength(): number {
    return this.form.get('message')?.value?.length || 0;
  }

  useTemplate(index: number): void {
    const template = this.templates[index];
    if (!template) return;
    this.form.patchValue({
      subject: template.subject,
      message: template.message
    });
  }

  save(): void {
    const sendSms = !!this.form.value.sendSms;
    const sendEmail = !!this.form.value.sendEmail;
    const email = this.form.value.email?.trim() || '';
    const subject = this.form.value.subject?.trim() || '';
    const message = this.form.value.message?.trim() || '';

    if (!sendSms && !sendEmail) {
      this.form.setErrors({ channelRequired: true });
      return;
    }

    if (this.form.errors?.['channelRequired']) {
      this.form.setErrors(null);
    }

    if (!message) {
      this.form.get('message')?.markAsTouched();
      return;
    }

    if (sendEmail) {
      this.form.get('email')?.setValidators([Validators.required, Validators.email]);
      this.form.get('subject')?.setValidators([Validators.required]);
    } else {
      this.form.get('email')?.clearValidators();
      this.form.get('subject')?.clearValidators();
    }

    this.form.get('email')?.updateValueAndValidity();
    this.form.get('subject')?.updateValueAndValidity();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close({
      contactNumber: this.data.client.contactNumber,
      sendSms,
      sendEmail,
      email,
      subject,
      message
    });
  }
}
